import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Shield, ShieldOff, ClipboardCheck, BadgeCheck, Sunrise, Sunset, Moon, MoonStar } from "lucide-react";
import { RAG_META } from "@/lib/rag";
import { todayLocal, fmtDate } from "@/lib/timefmt";

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const p2 = (x) => String(x).padStart(2, "0");
const addDays = (iso, n) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
};
const dayLbl = (iso) => { const d = new Date(`${iso}T00:00:00`); return `${DOW[d.getDay()]} ${d.getDate()}`; };

const sectionTitle = "text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-2";

function MyAstro({ today }) {
  const [astro, setAstro] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    api.get(`/astronomy?date=${today}`).then((r) => r.data?.no_coords ? setFailed(true) : setAstro(r.data)).catch(() => setFailed(true));
  }, [today]);
  if (failed || !astro) return null;
  const items = [
    { icon: Sunrise, label: "Sunrise", value: astro.sunrise, cls: "text-amber-400", tid: "my-astro-sunrise" },
    { icon: Sunset, label: "Sunset", value: astro.sunset, cls: "text-orange-400", tid: "my-astro-sunset" },
    { icon: Moon, label: "Moonrise", value: astro.moonrise, cls: "text-indigo-300", tid: "my-astro-moonrise" },
    { icon: Moon, label: "Moonset", value: astro.moonset, cls: "text-indigo-300", tid: "my-astro-moonset" },
    { icon: MoonStar, label: "Moon phase", value: `${astro.moon_phase} · ${astro.moon_illum}%`, cls: "text-slate-300", tid: "my-astro-moon-phase" },
  ];
  return (
    <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60 mb-8" data-testid="my-astro">
      <div className={sectionTitle}>Today — Sun &amp; Moon · {astro.location_name}</div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {items.map((it) => {
          const Icon = it.icon;
          return (
            <div key={it.label} className="flex items-center gap-2.5" data-testid={it.tid}>
              <Icon className={`h-4 w-4 shrink-0 ${it.cls}`} />
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">{it.label}</div>
                <div className="text-sm font-mono-num text-slate-200">{it.value || "—"}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function MyPage() {
  const { user, refresh } = useAuth();
  const taskLabel = user?.terminology?.task_label || "Task";
  const tasksLabel = user?.terminology?.task_label_plural || "Tasks";
  const terms = user?.terminology || {};
  const mp = user?.my_page || {};
  const sections = { details: true, fatigue7: true, plan7: true, post_task: true, auths: true, ...(mp.sections || {}) };
  const today = todayLocal();
  const isAdmin = user?.role === "admin" || !!user?.admin_level;
  const adminOn = user?.admin_mode !== false;

  const [entries, setEntries] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [commits, setCommits] = useState([]);
  const [authData, setAuthData] = useState({ pending: [], authorised_by_me: [], is_auth_officer: false });
  const [roleName, setRoleName] = useState("");
  const [showAuthored, setShowAuthored] = useState(false);

  const loadAuths = () => api.get("/tasks/pending-auth").then((r) => setAuthData(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/entries").then((r) => setEntries(r.data)).catch(() => {});
    api.get(`/tasks?from_date=${addDays(today, -30)}&to_date=${addDays(today, 6)}`).then((r) => setTasks(r.data)).catch(() => {});
    api.get(`/commitments?from_date=${today}&to_date=${addDays(today, 6)}`).then((r) => setCommits(r.data)).catch(() => {});
    loadAuths();
    if (user?.company_id) {
      api.get(`/roles?company_id=${user.company_id}`).then((r) => setRoleName((r.data.find((x) => x.id === user.role_id) || {}).name || "")).catch(() => {});
    }
  }, [user?.company_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const byDate = useMemo(() => Object.fromEntries(entries.map((e) => [e.date, e])), [entries]);
  const todayEntry = byDate[today];
  const todayMeta = todayEntry ? RAG_META[todayEntry.rag] : null;
  const last7 = Array.from({ length: 7 }, (_, i) => addDays(today, -(6 - i)));
  const next7 = Array.from({ length: 7 }, (_, i) => addDays(today, i));

  const upcomingTasks = useMemo(() => tasks.filter((t) => (t.end_date || t.date) >= today && t.status !== "cancelled"), [tasks, today]);
  const postable = useMemo(() => tasks
    .filter((t) => (t.end_date || t.date) <= today && (t.status === "authorised" || (t.status === "committed" && !t.requires_authorisation)))
    .sort((a, b) => b.date.localeCompare(a.date) || a.start.localeCompare(b.start)), [tasks, today]);
  const awaiting = authData.pending.filter((t) => t.mine || (authData.is_auth_officer && t.in_scope));

  const toggleAdmin = async () => {
    try {
      await api.post("/auth/admin-mode", { on: !adminOn });
      await refresh();
      toast.success(!adminOn ? "Admin mode on — editing enabled" : "Admin mode off — editing disabled");
    } catch (e) { toast.error("Could not change admin mode"); }
  };

  const actAuth = async (t, on) => {
    try {
      await api.post(`/tasks/${t.id}/authorise`, { on });
      toast.success(on ? `${taskLabel} authorised` : "Authorisation revoked");
      loadAuths();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const orgNames = user?.org_names || {};
  const orgChain = [
    [terms.unit_label || "Unit", orgNames.unit],
    [terms.subunit_label || "Subunit", orgNames.subunit],
    [terms.team_label || "Team", orgNames.team],
  ].filter(([, v]) => v);

  return (
    <div className="max-w-6xl" data-testid="my-page">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Member</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-1" data-testid="my-page-title">{mp.name || "My Page"}</h1>
          <p className="text-slate-400 mt-1 text-sm">Your readiness, plan and sign-offs at a glance.</p>
        </div>
        <div className="p-4 rounded-lg border border-slate-800 bg-slate-900/60 text-right min-w-[170px]" data-testid="my-today-score">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Today's fatigue score</div>
          {todayEntry ? (
            <div className="flex items-baseline justify-end gap-2.5 mt-1.5">
              <span className="text-4xl font-mono-num">{Number(todayEntry.score_total).toFixed(1)}</span>
              {todayMeta && (
                <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-widest ${todayMeta.cls}`}>
                  <span className={`inline-block h-1.5 w-1.5 rounded-full mr-1.5 align-middle ${todayMeta.dot}`} />
                  {todayMeta.label}
                </span>
              )}
            </div>
          ) : (
            <div className="mt-1.5 text-sm text-slate-400">No entry yet — <Link to="/fatigue" className="text-blue-400 hover:text-blue-300" data-testid="my-log-entry-link">log today's</Link></div>
          )}
        </div>
      </div>

      <MyAstro today={today} />

      {sections.details && (
        <div className="p-5 rounded-lg border border-slate-800 bg-slate-900/60 mb-8" data-testid="my-details">
          <div className={sectionTitle}>Member details</div>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-3 text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Name</div>
                <div className="text-slate-200 mt-0.5" data-testid="my-name">{user?.position ? `${user.position} ` : ""}{user?.first_name} {user?.last_name}</div>
              </div>
              {user?.position && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Rank / Position</div>
                  <div className="text-slate-200 mt-0.5" data-testid="my-position">{user.position}</div>
                </div>
              )}
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Crew role</div>
                <div className="text-slate-200 mt-0.5" data-testid="my-role">{roleName || "—"}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Email</div>
                <div className="text-slate-200 mt-0.5 truncate" data-testid="my-email">{user?.email}</div>
              </div>
              <div className="col-span-2">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Org placement</div>
                <div className="text-slate-200 mt-0.5" data-testid="my-org-chain">
                  {orgChain.length ? orgChain.map(([l, v]) => `${l}: ${v}`).join("  ·  ") : "Unassigned"}
                </div>
              </div>
            </div>
            {isAdmin && (
              <button onClick={toggleAdmin} data-testid="my-admin-mode-toggle"
                title="Toggle admin editing privileges for this session"
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-md text-xs transition-colors border shrink-0 ${
                  adminOn ? "bg-blue-600/15 text-blue-300 border-blue-600/40 hover:bg-blue-600/25" : "bg-slate-800/60 text-slate-400 border-slate-700 hover:text-slate-200"
                }`}>
                {adminOn ? <Shield className="h-3.5 w-3.5" /> : <ShieldOff className="h-3.5 w-3.5" />}
                Admin mode: {adminOn ? "On" : "Off"}
              </button>
            )}
          </div>
        </div>
      )}

      {sections.fatigue7 && (
        <div className="mb-8" data-testid="my-fatigue7">
          <div className={sectionTitle}>Fatigue — last 7 days</div>
          <div className="grid grid-cols-7 gap-1.5">
            {last7.map((d) => {
              const e = byDate[d];
              const meta = e ? RAG_META[e.rag] : null;
              return (
                <div key={d} className={`p-2.5 rounded-md border text-center ${e ? `border-slate-800 bg-slate-900/60 ${meta?.ring ? `ring-1 ${meta.ring}` : ""}` : "border-dashed border-slate-800 bg-slate-950/40"}`} data-testid={`my-fatigue-day-${d}`}>
                  <div className="text-[9px] uppercase tracking-widest text-slate-500 font-mono-num">{dayLbl(d)}</div>
                  {e ? (
                    <>
                      <div className="text-lg font-mono-num mt-1">{Number(e.score_total).toFixed(1)}</div>
                      <div className={`text-[9px] uppercase tracking-widest mt-0.5 ${meta?.cls || ""}`}>{meta?.label || ""}</div>
                    </>
                  ) : (
                    <div className="text-[10px] text-slate-600 mt-2">No entry</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {sections.plan7 && (
        <div className="mb-8" data-testid="my-plan7">
          <div className={sectionTitle}>Next 7 days — {tasksLabel} & Commitments</div>
          <div className="overflow-x-auto rounded-lg border border-slate-800">
            <div className="grid grid-cols-7 min-w-[980px]">
              {next7.map((d) => {
                const dTasks = upcomingTasks.filter((t) => t.date <= d && (t.end_date || t.date) >= d).sort((a, b) => a.start.localeCompare(b.start));
                const dCommits = commits.filter((c) => c.date === d).sort((a, b) => a.start.localeCompare(b.start));
                return (
                  <div key={d} className={`p-2 border-r border-slate-800/70 last:border-r-0 min-h-[110px] ${d === today ? "bg-blue-600/5" : ""}`} data-testid={`my-plan-day-${d}`}>
                    <div className={`text-[9px] uppercase tracking-widest font-mono-num mb-1.5 ${d === today ? "text-blue-300" : "text-slate-500"}`}>{dayLbl(d)}</div>
                    <div className="space-y-1">
                      {dTasks.map((t) => (
                        <div key={t.id} className="px-1.5 py-1 rounded bg-blue-600/10 border border-blue-600/25" data-testid={`my-plan-task-${t.id}`}>
                          <div className="text-[10px] font-mono-num text-blue-300">{t.start}–{t.end}</div>
                          <div className="text-[10px] text-slate-200 truncate">{t.name || t.task_type_name}</div>
                          <div className="text-[9px] text-slate-500 truncate">{t.task_type_name}</div>
                        </div>
                      ))}
                      {dCommits.map((c) => (
                        <div key={c.id} className="px-1.5 py-1 rounded border"
                          style={c.color ? { background: `${c.color}14`, borderColor: `${c.color}55` } : { background: "rgba(139,92,246,0.08)", borderColor: "rgba(139,92,246,0.3)" }}
                          data-testid={`my-plan-commitment-${c.id}`}>
                          <div className="text-[10px] font-mono-num" style={{ color: c.color || "#a78bfa" }}>{c.start}–{c.end}</div>
                          <div className="text-[10px] text-slate-200 truncate">{c.name || c.task_type_name}</div>
                          <div className="text-[9px] text-slate-500 truncate">{c.task_type_name}</div>
                        </div>
                      ))}
                      {!dTasks.length && !dCommits.length && <div className="text-[9px] text-slate-700">—</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {sections.post_task && (
        <div className="mb-8" data-testid="my-post-task">
          <div className={sectionTitle}>Available for Post {taskLabel} ({postable.length})</div>
          <div className="rounded-lg border border-slate-800 bg-slate-900/60 divide-y divide-slate-800/70">
            {postable.length === 0 && <div className="p-4 text-sm text-slate-500" data-testid="my-post-task-empty">Nothing waiting for post-{taskLabel.toLowerCase()}.</div>}
            {postable.slice(0, 10).map((t) => (
              <div key={t.id} className="px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap" data-testid={`my-post-task-row-${t.id}`}>
                <div className="flex items-center gap-2 flex-wrap text-sm">
                  <span className="text-slate-200">{t.name || t.task_type_name}</span>
                  <span className="text-[10px] text-slate-500">{t.task_type_name}</span>
                  <span className="text-xs font-mono-num text-slate-400">{fmtDate(t.date)} · {t.start}–{t.end}</span>
                </div>
                <Link to={`/post-task?task=${t.id}`} data-testid={`my-post-task-go-${t.id}`}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-blue-600/15 border border-blue-600/40 text-blue-300 text-xs hover:bg-blue-600/25 transition-colors">
                  <ClipboardCheck className="h-3 w-3" /> Post
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}

      {sections.auths && (
        <div data-testid="my-auths">
          <div className={sectionTitle}>Awaiting authorisation ({awaiting.length})</div>
          <div className="rounded-lg border border-slate-800 bg-slate-900/60 divide-y divide-slate-800/70">
            {awaiting.length === 0 && <div className="p-4 text-sm text-slate-500" data-testid="my-auths-empty">Nothing awaiting authorisation.</div>}
            {awaiting.map((t) => (
              <div key={t.id} className="px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap" data-testid={`my-auth-row-${t.id}`}>
                <div className="flex items-center gap-2 flex-wrap text-sm">
                  <span className="text-slate-200">{t.name || t.task_type_name}</span>
                  <span className="text-[10px] text-slate-500">{t.task_type_name}</span>
                  <span className="text-xs font-mono-num text-slate-400">{fmtDate(t.date)} · {t.start}–{t.end}</span>
                  {t.mine
                    ? <span className="px-2 py-0.5 rounded text-[9px] uppercase tracking-widest bg-blue-600/15 text-blue-300 border border-blue-600/40">Yours</span>
                    : <span className="text-[10px] text-slate-500">by {t.created_by_name}</span>}
                </div>
                {t.in_scope && (
                  <button onClick={() => actAuth(t, true)} data-testid={`my-auth-authorise-${t.id}`}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-violet-600/20 border border-violet-600/50 text-violet-200 text-xs hover:bg-violet-600/30 transition-colors">
                    <BadgeCheck className="h-3 w-3" /> Authorise
                  </button>
                )}
              </div>
            ))}
          </div>
          {authData.is_auth_officer && (
            <div className="mt-3">
              <button onClick={() => setShowAuthored((v) => !v)} data-testid="my-auth-show-authored"
                className="text-xs text-slate-400 hover:text-white transition-colors">
                {showAuthored ? "Hide" : "Show"} {tasksLabel.toLowerCase()} I've authorised that aren't post-{taskLabel.toLowerCase()} complete ({authData.authorised_by_me.length})
              </button>
              {showAuthored && (
                <div className="mt-2 rounded-lg border border-violet-600/30 bg-violet-600/5 divide-y divide-violet-600/15" data-testid="my-authored-list">
                  {authData.authorised_by_me.length === 0 && <div className="p-4 text-sm text-slate-500">None outstanding.</div>}
                  {authData.authorised_by_me.map((t) => (
                    <div key={t.id} className="px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap" data-testid={`my-authored-row-${t.id}`}>
                      <div className="flex items-center gap-2 flex-wrap text-sm">
                        <span className="text-slate-200">{t.name || t.task_type_name}</span>
                        <span className="text-xs font-mono-num text-slate-400">{fmtDate(t.date)} · {t.start}–{t.end}</span>
                        <span className="text-[10px] text-slate-500">by {t.created_by_name}</span>
                      </div>
                      <button onClick={() => actAuth(t, false)} data-testid={`my-authored-revoke-${t.id}`}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-slate-800 hover:bg-amber-900/40 text-slate-300 hover:text-amber-300 text-xs transition-colors">
                        Revoke
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
